import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://ballotr.com';
const SUPPORT_EMAIL = 'support@ballotr.com';

const deletionMailto =
  `mailto:${SUPPORT_EMAIL}` +
  '?subject=Ballotr%20Account%20Deletion%20Request' +
  '&body=Please%20delete%20my%20Ballotr%20account%20and%20associated%20personal%20data.%0A%0AAccount%20email%3A%20%0A%0AAdditional%20notes%3A%20';

const DeleteAccount: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Delete Account | Ballotr</title>
        <meta
          name="description"
          content="Request deletion of your Ballotr account and associated personal data."
        />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href={`${SITE_URL}/delete-account`} />
      </Helmet>

      <div className="min-h-screen bg-slate-50">
        <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="overflow-hidden rounded-3xl border border-sky-100 bg-white shadow-sm">
            <div className="border-b border-slate-200 bg-gradient-to-r from-sky-50 to-white px-6 py-8 sm:px-8">
              <p className="mb-2 text-sm font-semibold uppercase tracking-wide text-sky-700">
                Ballotr Account Support
              </p>
              <h1 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                Delete Your Ballotr Account
              </h1>
              <p className="mt-3 text-base leading-7 text-slate-600">
                You may request deletion of your Ballotr account and associated personal data.
              </p>
            </div>

            <div className="space-y-6 px-6 py-8 sm:px-8">
              <section>
                <h2 className="text-xl font-semibold text-slate-900">How to request deletion</h2>
                <p className="mt-3 text-base leading-7 text-slate-700">
                  To request deletion, contact Ballotr support using the email address associated
                  with your Ballotr account. This helps us verify and process the request.
                </p>

                <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-5">
                  <p className="text-sm font-semibold uppercase tracking-wide text-slate-700">
                    Support email
                  </p>
                  <p className="mt-2 text-base text-slate-800">{SUPPORT_EMAIL}</p>
                </div>

                <a
                  href={deletionMailto}
                  className="mt-5 inline-flex rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-white transition hover:bg-ballotr-blue-light"
                >
                  Request account deletion
                </a>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-slate-900">What to include</h2>
                <ul className="mt-3 space-y-2 pl-5 text-base leading-7 text-slate-700">
                  <li className="list-disc">The email address associated with your Ballotr account</li>
                  <li className="list-disc">A clear request to delete your account</li>
                  <li className="list-disc">Any additional context you want Ballotr support to know</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-slate-900">What happens next</h2>
                <p className="mt-3 text-base leading-7 text-slate-700">
                  When an account deletion request is completed, Ballotr deletes or anonymizes
                  associated personal profile data unless retention is required for legal, security,
                  fraud prevention, or compliance reasons.
                </p>
              </section>

              <section className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                <h2 className="text-xl font-semibold text-slate-900">Need help?</h2>
                <p className="mt-3 text-base leading-7 text-slate-700">
                  Questions about account deletion or privacy requests can be sent to{' '}
                  <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary hover:underline">
                    {SUPPORT_EMAIL}
                  </a>
                  .
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DeleteAccount;